// src/stores/adminAttendanceStore.js
import { defineStore } from "pinia";
import { ref } from "vue";
import svc from "@/services/hrm/adminAttendanceStats.service";
import { toast } from "@/utils/toast";
import { createScopedCache } from "./_cache";
import { MODULE_KEYS } from "@/config/modules-registry";
import { guardPlanModule } from "@/utils/planAccess";

const adminAttendanceStatsCache = createScopedCache(
  "dashboard:admin:attendance:stats"
);
const adminAttendanceRecentCache = createScopedCache(
  "dashboard:admin:attendance:recent"
);

const ADMIN_ATTENDANCE_STATS_TTL = 60 * 1000;
const ADMIN_ATTENDANCE_RECENT_TTL = 45 * 1000;

export const useAdminAttendanceStore = defineStore(
  "adminAttendance",
  () => {
    const stats = ref({
      total_employees: 0,
      present: 0,
      late: 0,
      absent: 0,
      on_leave: 0,
      permission: 0,
      uninformed: 0,
    });

    const recent = ref([]);

    const loadingStats = ref(false);
    const loadingRecent = ref(false);

    async function fetchStats(params = {}) {
      if (!(await guardPlanModule(MODULE_KEYS.HRM_ATTENDANCE))) {
        stats.value = {
          total_employees: 0,
          present: 0,
          late: 0,
          absent: 0,
          on_leave: 0,
          permission: 0,
          uninformed: 0,
        };
        return;
      }

      loadingStats.value = true;
      try {
        const payload = await adminAttendanceStatsCache.get(params, {
          ttlMs: ADMIN_ATTENDANCE_STATS_TTL,
          force: params.force === true,
          loader: async () => {
            const res = await svc.stats(params);
            return res.data || {
              total_employees: 0,
              present: 0,
              late: 0,
              absent: 0,
              on_leave: 0,
              permission: 0,
              uninformed: 0,
            };
          },
          onHydrate: (data) => {
            stats.value = data;
          },
        });
        stats.value = payload;
      } catch (e) {
        toast(
          e?.response?.data?.message || "Failed to load attendance stats",
          "error"
        );
      } finally {
        loadingStats.value = false;
      }
    }

    async function fetchRecent(params = {}) {
      loadingRecent.value = true;
      try {
        const payload = await adminAttendanceRecentCache.get(params, {
          ttlMs: ADMIN_ATTENDANCE_RECENT_TTL,
          force: params.force === true,
          loader: async () => {
            const res = await svc.latestClockings(params);
            return { rows: Array.isArray(res.data) ? res.data : [] };
          },
          onHydrate: ({ rows }) => {
            recent.value = rows || [];
          },
        });
        recent.value = payload.rows || [];
      } catch (e) {
        toast(
          e?.response?.data?.message || "Failed to load recent clock-ins",
          "error"
        );
      } finally {
        loadingRecent.value = false;
      }
    }

    async function ensureStats(params = {}) {
      return await fetchStats({ ...params, force: false });
    }

    async function ensureRecent(params = {}) {
      return await fetchRecent({ ...params, force: false });
    }

    function invalidateStats(params = null) {
      if (params) adminAttendanceStatsCache.invalidate(params);
      else adminAttendanceStatsCache.clear();
    }

    function invalidateRecent(params = null) {
      if (params) adminAttendanceRecentCache.invalidate(params);
      else adminAttendanceRecentCache.clear();
    }

    return {
      stats,
      recent,
      loadingStats,
      loadingRecent,
      fetchStats,
      fetchRecent,
      ensureStats,
      ensureRecent,
      invalidateStats,
      invalidateRecent,
    };
  }
);
